import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { BookingMessages } from "@/components/BookingMessages";
import { CancelDialog } from "@/components/CancelDialog";
import { imgSrc } from "@/lib/api";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  confirmed: "bg-[#FF4500]/10 text-[#FF4500]",
  pending: "bg-amber-100 text-amber-700",
  cancelled: "bg-neutral-100 text-neutral-500",
  completed: "bg-neutral-900 text-white",
};

export default function Trips() {
  const { user, loading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [msgBooking, setMsgBooking] = useState(null);
  const [cancelBooking, setCancelBooking] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  const payNow = async (b) => {
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: b.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-2">RENTER</div>
      <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-8">My trips</h1>

      {bookings.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-xl p-16 text-center">
          <div className="font-display font-bold text-xl">No trips yet</div>
          <div className="text-sm text-neutral-500 mt-2 mb-6">Book your first empty room.</div>
          <Button onClick={() => navigate("/search")} className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full">Explore spaces</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => (
            <div key={b.id} className="border border-neutral-200 rounded-xl p-6 bg-white flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid={`booking-row-${b.id}`}>
              <div>
                <div className="font-display font-bold text-lg">{b.listing_title}</div>
                <div className="text-xs text-neutral-500 mt-1">{b.listing_city}</div>
                <div className="mt-2 font-mono text-xs">{b.date} · {b.start_time}–{b.end_time} · {b.hours}h</div>
                {b.deposit_status === "claimed" && b.damage_claim && (
                  <div className="mt-3" data-testid={`claim-info-${b.id}`}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-amber-600">Deposit claim: ${(b.damage_claim.amount || 0).toFixed(2)}{b.damage_claim.renter_refund > 0 ? ` · $${b.damage_claim.renter_refund.toFixed(2)} refunded` : ""}</div>
                    {b.damage_claim.note && <div className="text-xs text-neutral-600 mt-1 max-w-md">{b.damage_claim.note}</div>}
                    <div className="flex gap-1.5 mt-2">
                      {(b.damage_claim.photos || []).map((p, i) => (
                        <img key={i} src={imgSrc(p)} alt="evidence" className="w-12 h-12 rounded object-cover border border-neutral-200" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-right">
                  <div className="font-display font-black text-xl">${b.total.toFixed(2)}</div>
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_STYLE[b.status] || "bg-neutral-100"}`}>
                    {b.status}
                  </span>
                  {b.deposit > 0 && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1" data-testid={`deposit-status-${b.id}`}>
                      Deposit ${b.deposit.toFixed(0)} · {b.deposit_status === "released" ? "refunded" : b.deposit_status === "claimed" ? `$${(b.deposit_claim_amount || 0).toFixed(0)} claimed` : b.deposit_status === "held" ? "held" : "—"}
                    </div>
                  )}
                  {b.status === "cancelled" && b.cancellation && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1" data-testid={`refund-${b.id}`}>
                      {b.cancellation.refund_amount > 0 ? `Refunded $${b.cancellation.refund_amount.toFixed(0)}` : "No refund"}
                    </div>
                  )}
                </div>
                {b.status === "pending" && b.payment_status !== "paid" && (
                  <Button onClick={() => payNow(b)} className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full" data-testid={`pay-now-${b.id}`}>
                    Complete payment
                  </Button>
                )}
                {b.status !== "cancelled" && (
                  <Button variant="outline" onClick={() => setMsgBooking(b)} className="gap-2" data-testid={`message-host-${b.id}`}>
                    <MessageCircle className="w-4 h-4" /> Message host
                  </Button>
                )}
                {b.status === "confirmed" && (
                  <Button variant="outline" onClick={() => setCancelBooking(b)} data-testid={`cancel-${b.id}`}>Cancel</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <BookingMessages
        booking={msgBooking}
        open={!!msgBooking}
        onOpenChange={(o) => !o && setMsgBooking(null)}
        counterpart="Chat with host"
      />
      <CancelDialog
        booking={cancelBooking}
        open={!!cancelBooking}
        onOpenChange={(o) => !o && setCancelBooking(null)}
        onDone={(id, data) => setBookings((bs) => bs.map((b) => b.id === id ? { ...b, status: "cancelled", cancellation: { refund_amount: data.refund_amount } } : b))}
      />
    </div>
  );
}
